import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import './PartnerApplication.css';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://clothing-matcher-api.vercel.app';

const PartnerApplication = () => {
  const [formData, setFormData] = useState({
    email: '',
    website_url: '',
    traffic_description: '',
    instagram: '',
    tiktok: '',
    youtube: '',
    twitter: ''
  });

  const [status, setStatus] = useState('idle'); // idle, loading, success, error, already_exists
  const [responseMessage, setResponseMessage] = useState('');
  const [applicationId, setApplicationId] = useState('');
  const [partnerId, setPartnerId] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    setResponseMessage('');

    // Build social handles object
    const socialHandles = {};
    if (formData.instagram) socialHandles.instagram = formData.instagram;
    if (formData.tiktok) socialHandles.tiktok = formData.tiktok;
    if (formData.youtube) socialHandles.youtube = formData.youtube;
    if (formData.twitter) socialHandles.twitter = formData.twitter;

    try {
      const response = await fetch(`${API_BASE_URL}/api/partner/apply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          website_url: formData.website_url,
          traffic_description: formData.traffic_description,
          social_handles: Object.keys(socialHandles).length > 0 ? socialHandles : null
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setStatus('success');
        setResponseMessage(data.message);
        setApplicationId(data.application_id || '');

        // Extract partner ID from message if auto-approved
        const partnerIdMatch = data.message?.match(/PTR_[A-Z0-9]+/);
        if (partnerIdMatch) {
          setPartnerId(partnerIdMatch[0]);
        }
      } else if (data.status === 'already_approved' || data.status === 'pending') {
        setStatus('already_exists');
        setResponseMessage(data.message);
        setApplicationId(data.application_id || '');
      } else {
        setStatus('error');
        setResponseMessage(data.message || data.detail || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      console.error('Application error:', error);
      setStatus('error');
      setResponseMessage('Network error. Please check your connection and try again.');
    }
  };

  return (
    <div className="partner-page">
      <Helmet>
        <title>Become a Partner | Fantomize Affiliate Program</title>
        <meta name="description" content="Join Fantomize's affiliate program. Earn commissions by promoting luxury fashion visual search. Get started in minutes." />
      </Helmet>

      <div className="partner-container">
        {/* Hero Section */}
        <div className="partner-hero">
          <h1>Become a Partner</h1>
          <p className="partner-subtitle">
            Join Fantomize's affiliate program and earn commissions on every purchase made through your links.
          </p>
        </div>

        {/* Benefits Section */}
        <div className="partner-benefits">
          <div className="benefit-card">
            <div className="benefit-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
              </svg>
            </div>
            <h3>Competitive Earnings</h3>
            <p>Industry-leading commission rates that reward your success</p>
          </div>
          <div className="benefit-card">
            <div className="benefit-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
              </svg>
            </div>
            <h3>Simple Integration</h3>
            <p>Generate tracked affiliate links in seconds</p>
          </div>
          <div className="benefit-card">
            <div className="benefit-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M3 3v18h18"/>
                <path d="M18 17V9M13 17V5M8 17v-3"/>
              </svg>
            </div>
            <h3>Real-time Analytics</h3>
            <p>Track performance and earnings in your dashboard</p>
          </div>
          <div className="benefit-card">
            <div className="benefit-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                <polyline points="22 4 12 14.01 9 11.01"/>
              </svg>
            </div>
            <h3>Instant Approval</h3>
            <p>Start earning immediately — no waiting period</p>
          </div>
        </div>

        {/* Application Form */}
        <div className="partner-form-section">
          <h2>Apply Now</h2>

          {status === 'success' ? (
            <div className="success-message">
              <div className="success-icon">🎉</div>
              <h3>Congratulations!</h3>
              <p>{responseMessage}</p>
              {partnerId && (
                <div className="partner-id-display">
                  <span>Your Partner ID:</span>
                  <code>{partnerId}</code>
                </div>
              )}
              <div className="next-steps">
                <h4>Next Steps:</h4>
                <ol>
                  <li>Generate your API key using your email</li>
                  <li>Use the API to create affiliate links</li>
                  <li>Share links and start earning!</li>
                </ol>
              </div>
              <Link to="/" className="back-home-btn">
                Back to Home
              </Link>
            </div>
          ) : status === 'already_exists' ? (
            <div className="already-exists-message">
              <div className="info-icon">ℹ️</div>
              <h3>Application Found</h3>
              <p>{responseMessage}</p>
              {applicationId && (
                <p className="application-id">Application ID: <code>{applicationId}</code></p>
              )}
              <Link to="/" className="back-home-btn">
                Back to Home
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="partner-form">
              <div className="form-group">
                <label htmlFor="email">Email Address *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="you@example.com"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="website_url">Website or Main Social Profile *</label>
                <input
                  type="url"
                  id="website_url"
                  name="website_url"
                  value={formData.website_url}
                  onChange={handleChange}
                  placeholder="https://yourwebsite.com or https://instagram.com/yourhandle"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="traffic_description">Describe Your Audience *</label>
                <textarea
                  id="traffic_description"
                  name="traffic_description"
                  value={formData.traffic_description}
                  onChange={handleChange}
                  placeholder="Tell us about your audience, traffic sources, and how you plan to promote Fantomize. (minimum 20 characters)"
                  rows={4}
                  minLength={20}
                  required
                />
              </div>

              <div className="social-handles-section">
                <h4>Social Media Handles (Optional)</h4>
                <div className="social-grid">
                  <div className="form-group">
                    <label htmlFor="instagram">Instagram</label>
                    <input
                      type="text"
                      id="instagram"
                      name="instagram"
                      value={formData.instagram}
                      onChange={handleChange}
                      placeholder="@yourhandle"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="tiktok">TikTok</label>
                    <input
                      type="text"
                      id="tiktok"
                      name="tiktok"
                      value={formData.tiktok}
                      onChange={handleChange}
                      placeholder="@yourhandle"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="youtube">YouTube</label>
                    <input
                      type="text"
                      id="youtube"
                      name="youtube"
                      value={formData.youtube}
                      onChange={handleChange}
                      placeholder="@yourchannel"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="twitter">X (Twitter)</label>
                    <input
                      type="text"
                      id="twitter"
                      name="twitter"
                      value={formData.twitter}
                      onChange={handleChange}
                      placeholder="@yourhandle"
                    />
                  </div>
                </div>
              </div>

              {status === 'error' && (
                <div className="error-message">
                  <span>⚠️</span> {responseMessage}
                </div>
              )}

              <button
                type="submit"
                className="submit-btn"
                disabled={status === 'loading'}
              >
                {status === 'loading' ? (
                  <>
                    <span className="spinner"></span>
                    Submitting...
                  </>
                ) : (
                  'Apply to Become a Partner'
                )}
              </button>

              <p className="form-note">
                By applying, you agree to our <Link to="/terms.html" target="_blank">Terms of Service</Link> and <Link to="/affiliate-disclosure.html" target="_blank">Affiliate Disclosure</Link>.
              </p>
            </form>
          )}
        </div>

        {/* How It Works */}
        <div className="how-it-works">
          <h2>How It Works</h2>
          <div className="steps">
            <div className="step">
              <div className="step-number">1</div>
              <h3>Apply</h3>
              <p>Fill out the form above with your details</p>
            </div>
            <div className="step">
              <div className="step-number">2</div>
              <h3>Get Approved</h3>
              <p>Instant approval - start immediately</p>
            </div>
            <div className="step">
              <div className="step-number">3</div>
              <h3>Generate Links</h3>
              <p>Create tracked links for any product</p>
            </div>
            <div className="step">
              <div className="step-number">4</div>
              <h3>Earn</h3>
              <p>Get paid for every purchase through your links</p>
            </div>
          </div>
        </div>

        {/* FAQ */}
        <div className="partner-faq">
          <h2>Frequently Asked Questions</h2>
          <div className="faq-list">
            <div className="faq-item">
              <h4>How do I get paid?</h4>
              <p>Payments are made monthly via PayPal or bank transfer once you reach the $50 minimum threshold. There's a 60-day verification period for fraud protection.</p>
            </div>
            <div className="faq-item">
              <h4>What's the cookie duration?</h4>
              <p>30 days. If a customer clicks your link and purchases within 30 days, you earn the commission.</p>
            </div>
            <div className="faq-item">
              <h4>Can I promote on social media?</h4>
              <p>Absolutely! Instagram, TikTok, YouTube, Twitter, Pinterest - wherever your audience is.</p>
            </div>
            <div className="faq-item">
              <h4>How do I track my performance?</h4>
              <p>Use your API key to access the dashboard endpoint, or we'll build a visual dashboard soon.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PartnerApplication;
